import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../lib/api';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { SectionCard } from './SectionCard';
import { Plus, Trash2, Edit, Save, X } from 'lucide-react';

export default function TestimonialsManager({ onSaved, toast }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get('/admin/testimonials'); setItems(data); } catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const startNew = () => { setEditing({ name: '', role: 'Happy Customer', avatar: '', message: '', sort_order: items.length, active: true }); setShowForm(true); };
  const cancel = () => { setEditing(null); setShowForm(false); };

  const save = async () => {
    if (!editing.name || !editing.message) {
      toast({ title: 'Name and Message are required', variant: 'destructive' }); return;
    }
    try {
      if (editing.id) await api.put(`/admin/testimonials/${editing.id}`, editing);
      else await api.post('/admin/testimonials', editing);
      toast({ title: 'Saved' });
      cancel(); load(); onSaved && onSaved();
    } catch (err) {
      toast({ title: 'Failed', description: err?.response?.data?.detail, variant: 'destructive' });
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete?')) return;
    await api.delete(`/admin/testimonials/${id}`);
    toast({ title: 'Deleted' });
    load(); onSaved && onSaved();
  };

  return (
    <SectionCard title="Testimonials" subtitle={`${items.length} reviews`} action={<button data-testid="add-testimonial-btn" onClick={startNew} className="btn-brand text-sm"><Plus className="w-4 h-4" /> Add</button>}>
      {showForm && (
        <div className="mb-4 p-4 border border-[#f7941e]/30 bg-[#fff7e6] rounded-lg space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>Customer Name *</Label><Input value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })} /></div>
            <div><Label>Role / Designation</Label><Input value={editing.role} onChange={e => setEditing({ ...editing, role: e.target.value })} /></div>
          </div>
          <div><Label>Avatar Image URL</Label><Input value={editing.avatar} onChange={e => setEditing({ ...editing, avatar: e.target.value })} /></div>
          <div><Label>Message *</Label><Textarea rows={3} value={editing.message} onChange={e => setEditing({ ...editing, message: e.target.value })} /></div>
          <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={editing.active} onChange={e => setEditing({ ...editing, active: e.target.checked })} /> Active</label>
          <div className="flex gap-2">
            <button onClick={save} className="btn-brand text-sm"><Save className="w-4 h-4" /> Save</button>
            <button onClick={cancel} className="inline-flex items-center gap-1.5 text-sm bg-gray-100 hover:bg-gray-200 px-4 py-2 rounded"><X className="w-4 h-4" /> Cancel</button>
          </div>
        </div>
      )}
      <div className="space-y-2">
        {items.map(it => (
          <div key={it.id} className="border border-gray-200 rounded-lg p-3 flex gap-3">
            {it.avatar && <img src={it.avatar} alt={it.name} className="w-12 h-12 rounded-full object-cover border-2 border-[#f7941e] shrink-0" />}
            <div className="flex-1 min-w-0">
              <p className="font-display font-semibold">{it.name}</p>
              <p className="text-xs text-[#f7941e]">{it.role}</p>
              <p className="text-sm text-gray-600 mt-1">{it.message}</p>
            </div>
            <div className="flex flex-col gap-1">
              <button onClick={() => { setEditing({ ...it }); setShowForm(true); }} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"><Edit className="w-4 h-4" /></button>
              <button onClick={() => remove(it.id)} className="p-1.5 text-red-600 hover:bg-red-50 rounded"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </SectionCard>
  );
}
